
/**
 * PdvDesktop.vue
 *
 * Tela de PDV para o modo Desktop (Electron).
 *
 * Estratégia: extends Form.vue via JS spread.
 *   - Herda template (render), componentes, mixins, watchers e métodos do Form.vue.
 *   - Substitui SOMENTE os comportamentos que diferem no desktop:
 *       • created()        → carrega configurações da store local (sem chamada API)
 *       • mounted()        → guarda que só roda em modo desktop
 *       • search()         → busca produtos no SQLite local
 *       • searchClient()   → busca clientes no SQLite local
 *       • finalizarVenda() → delega para VendaPdvDesktopService
 *   - getPagamentos() já é resolvido pelo PagamentosMixin herdado
 *     (ele detecta isDesktopMode() automaticamente).
 *
 * O PDV Web (Form.vue) NÃO é alterado.
 */
import _ from "lodash";
import FormVue from "@/modules/Vendas/pages/Form.vue";
import VendaPdvDesktopService from "../services/VendaPdvDesktopService";

// ── Preserva hooks do Form.vue antes do spread ─────────────────────────────
const _origCreated      = FormVue.created;
const _origMounted      = FormVue.mounted;
const _origBeforeDestroy = FormVue.beforeDestroy;

export default {
  // ── Herda tudo do Form.vue (render, staticRenderFns, data, mixins, etc.) ─
  ...FormVue,

  // ── Identificação ──────────────────────────────────────────────────────
  name: "PdvDesktop",

  // ── Lifecycle: created ─────────────────────────────────────────────────
  /**
   * Em modo web: repassa ao created() original do Form.vue.
   * Em modo desktop: carrega configurações locais sem chamada HTTP.
   */
  async created() {
    if (!this.$isDesktop) {
      // Modo web – comportamento original do Form.vue
      if (_origCreated) await _origCreated.call(this);
      return;
    }

    // Modo Desktop ─────────────────────────────────────────────────────
    this.setLoading(true);
    try {
      if (this.$route.params.id) {
        this.id       = this.$route.params.id;
        this.nota.id  = parseInt(this.$route.params.id);
      }

      const api = this.$electron;
      const [serie, infoCompl, solicitarCartao] = await Promise.all([
        api.config.get("serie_nfe"),
        api.config.get("informacoes_complementares"),
        api.config.get("solicitar_dados_cartao"),
      ]);

      this.serie                    = serie        || "1";
      this.informacoesComplementares = infoCompl    || "";
      this.solicitarDadosCartao      = !!solicitarCartao;
      this.venda.observation         = this.informacoesComplementares;
    } catch (e) {
      console.error("[PdvDesktop] Erro ao carregar config local:", e);
    } finally {
      this.setLoading(false);
    }
  },

  // ── Lifecycle: mounted ─────────────────────────────────────────────────
  mounted() {
    // Guarda: bloqueia acesso no navegador web
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }
    // Executa o mounted() original do Form.vue (atalhos de teclado, etc.)
    if (_origMounted) _origMounted.call(this);
  },

  // ── Lifecycle: beforeDestroy ───────────────────────────────────────────
  beforeDestroy() {
    if (_origBeforeDestroy) _origBeforeDestroy.call(this);
  },

  // ── Methods ────────────────────────────────────────────────────────────
  methods: {
    // Herda todos os métodos do Form.vue
    ...FormVue.methods,

    /**
     * Override: busca produtos no SQLite local.
     * O Form.vue original chama /produtos/filter-item na API.
     */
    search: _.debounce(async (loading, search, vm) => {
      if (!search || search === "") {
        loading(false);
        return;
      }

      loading(true);
      try {
        const results = (await vm.$electron.produtos.search(search)) || [];
        vm.produtos   = results;

        // Auto-adiciona se for código de barras único
        if (results.length === 1 && vm.isBarCode(search) && Number.parseInt(search)) {
          vm.searchCodeBar = search;
          vm.setProduto(results[0], true);
        }
      } catch (e) {
        console.error("[PdvDesktop] Erro na busca de produtos:", e);
        vm.produtos = [];
      }
      loading(false);
    }, 350),

    /**
     * Override: busca clientes no SQLite local.
     */
    searchClient: _.debounce(async (loading, search, vm) => {
      try {
        vm.clientes = (await vm.$electron.clientes.search(search)) || [];
      } catch (e) {
        console.error("[PdvDesktop] Erro na busca de clientes:", e);
        vm.clientes = [];
      }
      loading(false);
    }, 500),

    /**
     * Override: finalização de venda via VendaPdvDesktopService.
     *
     * Substitui completamente o finalizarVenda() do Form.vue para o fluxo
     * desktop. O Form.vue original não é alterado.
     */
    async finalizarVenda(transmitirVenda) {
      if (this.store.pafNFCe && !transmitirVenda) return;
      if (this.total === 0) return false;

      // Monta pagamentos
      this.venda.paymentModeCollection = this.listaPagamentos;
      if (this.venda.paymentModeCollection.length === 0) {
        await this.defaultPagamento();
      }

      // Ajusta troco e total
      const troco          = this.roundTo2Digits(this.valorPago - this.total);
      this.venda.remainder = troco > 0 ? troco : 0;
      this.venda.price     = this.total;
      this.venda.isNFce    = transmitirVenda;

      // Filtra itens ativos
      this.venda.productOfTradeCollection =
        this.store.productOfTradeCollection.filter((item) =>
          String(item.active).includes("true")
        );

      const payload = { ...this.venda, serie: this.serie };

      this.setLoading(true);
      this.isLoadingSubmit = true;

      try {
        await VendaPdvDesktopService.finalizarVenda(payload, {
          onSynced:  () => this.$toast.success("Venda sincronizada com o servidor!"),
          onPending: () => this.$toast.warning("Sem conexão. Venda salva como pendente."),
        });

        this.abrirVenda();
        this.novaVenda();
      } catch (e) {
        console.error("[PdvDesktop] Erro ao finalizar venda:", e);
        this.$toast.error(`Erro ao finalizar venda: ${e.message}`);
      } finally {
        this.setLoading(false);
        this.isLoadingSubmit = false;
        this.closeInfoVendaModal?.();
        this.valorPagoVenda = 0;
      }
    },
  },
};
